import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Users, Activity, CheckCircle2, AlertTriangle, CalendarDays } from "lucide-react";

function elapsed(pickup) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(pickup).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");

  const load = useCallback(() => {
    const params = clientFilter !== "all" ? { client_id: clientFilter } : {};
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [clientFilter]);

  useEffect(() => {
    if (user?.role === "super_admin") api.get("/clients").then((r) => setClients(r.data));
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data)
    return (
      <div className="space-y-6" data-testid="dashboard-skeleton">
        <div className="skeleton h-10 w-72" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-32" />)}
        </div>
        <div className="skeleton h-72" />
      </div>
    );

  const s = data.stats;
  const cards = [
    { label: "In Service Now", value: s.in_service_now, icon: Activity, chip: "bg-emerald-50 text-emerald-600", testid: "stat-in-service" },
    { label: "Returned Today", value: s.returned_today, icon: CheckCircle2, chip: "bg-sky-50 text-sky-600", testid: "stat-returned-today" },
    { label: "Active Providers", value: s.active_providers ?? 0, icon: Users, chip: "bg-primary/10 text-primary", testid: "stat-active-providers" },
    { label: "Pending Returns", value: s.missing_returns, icon: AlertTriangle, chip: "bg-amber-50 text-amber-600", testid: "stat-missing-returns" },
    { label: "Today's Verifications", value: s.verifications_today, icon: CalendarDays, chip: "bg-[#C7A34A]/15 text-[#8a6d23]", testid: "stat-today" },
  ];

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Operations Overview</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Live Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Real-time service verification · auto-refreshes every 30s</p>
        </div>
        {user?.role === "super_admin" && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-64 bg-white" data-testid="dashboard-client-filter">
              <SelectValue placeholder="All clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
          {cards.map((c, idx) => (
            <div key={c.label} className={`fade-up group relative p-5 transition-colors duration-200 hover:bg-slate-50/70 ${idx > 0 ? "border-slate-100 max-xl:border-t md:max-xl:border-l xl:border-l" : ""}`}
              style={{ animationDelay: `${idx * 60}ms` }} data-testid={c.testid}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{c.label}</span>
                <div className={`flex h-7 w-7 items-center justify-center rounded-md ${c.chip}`}>
                  <c.icon size={14} />
                </div>
              </div>
              <div className="mt-3 font-display text-[34px] font-bold leading-none tracking-tight text-slate-900">{c.value}</div>
            </div>
          ))}
        </div>
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="pulse-dot h-2.5 w-2.5 rounded-full bg-emerald-500" />
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">Currently In Service</h2>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 font-mono text-xs text-slate-500" data-testid="in-service-count">{data.in_service.length} active</span>
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
          <Table data-testid="in-service-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Student</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>School</TableHead>
                <TableHead>Time Out</TableHead>
                <TableHead>Elapsed</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.in_service.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">No students currently in service</TableCell></TableRow>
              )}
              {data.in_service.map((v) => (
                <TableRow key={v.id} data-testid={`in-service-row-${v.student_external_id}`}>
                  <TableCell className="font-medium">{v.student_name}</TableCell>
                  <TableCell>{v.provider_name}</TableCell>
                  <TableCell>{v.service}</TableCell>
                  <TableCell className="text-slate-500">{v.school_name}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(v.pickup_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{elapsed(v.pickup_time)}</TableCell>
                  <TableCell>
                    {v.missing_alerted ? (
                      <Badge className="gap-1.5 rounded-full border-0 bg-red-50 text-red-700 hover:bg-red-50"><span className="h-1.5 w-1.5 rounded-full bg-red-500" />Overdue</Badge>
                    ) : (
                      <Badge className="gap-1.5 rounded-full border-0 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />In Service</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">Returned Today</h2>
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 font-mono text-xs text-slate-500" data-testid="returned-count">{data.returned_today.length} completed</span>
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
          <Table data-testid="returned-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Student</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Time Out</TableHead>
                <TableHead>Time In</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.returned_today.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">No returns yet today</TableCell></TableRow>
              )}
              {data.returned_today.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="font-medium">{v.student_name}</TableCell>
                  <TableCell>{v.provider_name}</TableCell>
                  <TableCell>{v.service}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(v.pickup_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(v.return_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{v.duration_minutes}m</TableCell>
                  <TableCell>
                    {v.late_return ? (
                      <Badge className="gap-1.5 rounded-full border-0 bg-amber-50 text-amber-700 hover:bg-amber-50"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />Late Return</Badge>
                    ) : (
                      <Badge className="gap-1.5 rounded-full border-0 bg-sky-50 text-sky-700 hover:bg-sky-50"><span className="h-1.5 w-1.5 rounded-full bg-sky-500" />Returned</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
